import { Injectable } from '@angular/core';
import {
	INSTALLATION_ID_KEY,
	PUSH_BINDING_DB_NAME,
	PUSH_BINDING_RECORD_KEY,
	PUSH_BINDING_STORE_NAME
} from '../config/push-constants';

/**
 * Holds the two identifiers that keep this browser profile's notifications straight.
 *
 * The installation id is generated once per browser profile and kept in localStorage. It is what the
 * backend uses to recognise this browser: the User-Agent it used to key on carries the Chrome
 * version, so every browser update registered what looked like a brand new device.
 *
 * The binding id is issued by the backend at every registration and repeated in every push. When
 * someone else signs in on this workstation the backend issues a new one, so an alert already in
 * flight for the previous user arrives with a stale binding and is discarded unshown. It is mirrored
 * into IndexedDB because the service worker - which decides whether to display a push - cannot read
 * localStorage.
 */
@Injectable()
export class PushIdentityService {
	/** Returns the installation id for this browser profile, creating one on first call. */
	public getOrCreateInstallationId(): string {
		let installationId = localStorage.getItem(INSTALLATION_ID_KEY);

		if (!installationId) {
			installationId = PushIdentityService.newUuid();
			localStorage.setItem(INSTALLATION_ID_KEY, installationId);
		}

		return installationId;
	}

	/** Mirrors the binding into IndexedDB so the service worker can check incoming pushes against it. */
	public setBinding(bindingId: string | null): Promise<void> {
		return this.withStore('readwrite', store => {
			if (bindingId) {
				store.put({ id: PUSH_BINDING_RECORD_KEY, bindingId });
			} else {
				store.delete(PUSH_BINDING_RECORD_KEY);
			}
		});
	}

	/** Clears the binding on sign-out, so anything still queued for that user is rejected on arrival. */
	public clearBinding(): Promise<void> {
		return this.setBinding(null);
	}

	private withStore(mode: IDBTransactionMode, work: (store: IDBObjectStore) => void): Promise<void> {
		if (!('indexedDB' in window)) {
			return Promise.resolve();
		}

		return new Promise<void>(resolve => {
			const open = indexedDB.open(PUSH_BINDING_DB_NAME, 1);

			open.onupgradeneeded = () => {
				const db = open.result;
				if (!db.objectStoreNames.contains(PUSH_BINDING_STORE_NAME)) {
					db.createObjectStore(PUSH_BINDING_STORE_NAME, { keyPath: 'id' });
				}
			};

			open.onsuccess = () => {
				const db = open.result;
				try {
					const transaction = db.transaction(PUSH_BINDING_STORE_NAME, mode);
					work(transaction.objectStore(PUSH_BINDING_STORE_NAME));
					transaction.oncomplete = () => { db.close(); resolve(); };
					transaction.onerror = () => { db.close(); resolve(); };
				} catch {
					db.close();
					resolve();
				}
			};

			// Storage being unavailable (private mode, a blocked upgrade) must not break sign-in. The
			// service worker treats a missing binding as "accept", which is the behaviour we had before.
			open.onerror = () => resolve();
			open.onblocked = () => resolve();
		});
	}

	private static newUuid(): string {
		// randomUUID is not in this project's TypeScript DOM lib yet, though every browser we support
		// on modern carehome workstations has it.
		const webCrypto = typeof crypto !== 'undefined' ? crypto as Crypto & { randomUUID?: () => string } : null;
		if (webCrypto && typeof webCrypto.randomUUID === 'function') {
			return webCrypto.randomUUID();
		}

		// Older browsers on carehome workstations do not all have randomUUID.
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
			const random = Math.random() * 16 | 0;
			const value = character === 'x' ? random : (random & 0x3 | 0x8);
			return value.toString(16);
		});
	}
}
